import React from 'react';
import { Link } from 'react-router-dom';
import { Code2, Rocket, Target, ArrowRight, CheckCircle2, Users, Brain, GitBranch, Shield, Clock } from 'lucide-react';

const HomePage = () => {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      if (result.success) {
        alert('Message sent!');
        (e.target as HTMLFormElement).reset();
      } else {
        alert('Something went wrong.');
      }
    } catch (error) {
      alert('Error sending message. Please try again.');
    }
  };

  const technologies = [
    { name: 'React', percentage: 95 },
    { name: 'Node.js', percentage: 90 },
    { name: 'TypeScript', percentage: 92 },
    { name: 'Cloud Architecture', percentage: 88 },
  ];

  const benefits = [
    {
      title: "Faster Time to Market",
      description: "Launch your product up to 70% faster with our agile development approach",
      metric: "70%"
    },
    {
      title: "Cost Reduction",
      description: "Save on development and maintenance costs through efficient processes",
      metric: "40%"
    },
    {
      title: "Customer Satisfaction",
      description: "Our solutions consistently exceed client expectations",
      metric: "100%"
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <header className="relative min-h-screen bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative container mx-auto px-6 pt-32 pb-24">
          <div className="max-w-4xl mx-auto text-center">
            <div id="hero-logo" className="inline-block mb-8">
              <img 
                src="https://stingerbuilds.com/Stinger-builds-software-logo-transparent.png"
                alt="Stinger Builds"
                className="h-24 w-auto mx-auto"
              />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Transform Your Business with
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500"> Custom Software</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">Join 200+ businesses that have increased their revenue by an average of <span className="text-yellow-400 font-bold">150%</span> with our custom software solutions.</p>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-lg p-6">
                  <div className="text-3xl font-bold text-yellow-400 mb-2">{benefit.metric}</div>
                  <h3 className="font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-400">{benefit.description}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/contact"
                className="w-full sm:w-auto px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all flex items-center justify-center group"
              >
                Get Free Consultation
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/case-studies"
                className="w-full sm:w-auto px-8 py-4 border-2 border-white/20 text-white rounded-lg font-semibold hover:bg-white/10 transition-all"
              >
                View Case Studies
              </Link>
            </div>
          </div>

          {/* Social Proof Section */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-gray-400">Trusted by Industry Leaders</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
              {technologies.map((tech, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-lg p-6 w-full">
                  <h3 className="text-lg font-semibold mb-4">{tech.name}</h3>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-1000"
                      style={{ width: `${tech.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Solutions Section */}
      <section id="solutions" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <GitBranch className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Enterprise Solutions</h2>
            <p className="text-lg text-gray-600">
              We specialize in building scalable, secure, and efficient software solutions
              that drive business growth and operational excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Code2 className="w-8 h-8 text-yellow-400" />,
                title: "Custom Development",
                description: "Bespoke software solutions tailored to your unique business processes and objectives.",
                features: ["API Integration", "Cloud Architecture", "Mobile Apps"]
              },
              {
                icon: <Shield className="w-8 h-8 text-yellow-400" />,
                title: "Data Solutions",
                description: "Robust data management systems that ensure security, accessibility, and scalability.",
                features: ["Data Migration", "Analytics", "Real-time Processing"]
              },
              {
                icon: <Target className="w-8 h-8 text-yellow-400" />,
                title: "Digital Transformation",
                description: "Strategic technological evolution that positions your business for future success.",
                features: ["Process Automation", "Legacy Modernization", "Cloud Migration"]
              }
            ].map((solution, index) => (
              <div 
                key={index}
                className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="mb-6">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{solution.title}</h3>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-3">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-sm text-gray-500">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section id="approach" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Our Development Process</h2>
                <p className="text-lg text-gray-600 mb-8">
                  We follow a systematic approach to software development that ensures
                  quality, efficiency, and successful project delivery.
                </p>
                <div className="space-y-6">
                  {[
                    {
                      title: "Discovery & Planning",
                      description: "Deep analysis of requirements and architectural planning"
                    },
                    {
                      title: "Agile Development",
                      description: "Iterative development with continuous feedback and adaptation"
                    },
                    {
                      title: "Quality Assurance",
                      description: "Rigorous testing and performance optimization"
                    },
                    {
                      title: "Deployment & Support",
                      description: "Smooth deployment and ongoing maintenance"
                    }
                  ].map((step, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center text-slate-900 font-semibold">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <Link
                    to="/approach"
                    className="inline-flex items-center px-6 py-3 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
                  >
                    Learn More About Our Approach
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80" 
                  alt="Team collaboration" 
                  className="rounded-xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white rounded-lg shadow-xl p-6 max-w-xs">
                  <div className="flex items-center space-x-2 mb-4">
                    <Rocket className="w-6 h-6 text-yellow-400" />
                    <span className="font-semibold">Project Success Rate</span>
                  </div>
                  <div className="text-4xl font-bold text-slate-900">100%</div>
                  <p className="text-sm text-gray-600 mt-2">Based on client satisfaction</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-12">
                  <h2 className="text-3xl font-bold mb-6">Start Your Project</h2>
                  <p className="text-gray-600 mb-8">
                    Ready to transform your business with custom software solutions?
                    Let's discuss your project and explore how we can help.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Project Details
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="Tell us about your project"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all flex items-center justify-center group"
                    >
                      Send Message
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </form>
                </div>
                <div className="bg-slate-900 p-12 flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-8">Why Choose Us?</h3>
                    <div className="space-y-6">
                      {[
                        "Expert development team with proven track record",
                        "Cutting-edge technology stack and best practices",
                        "Transparent communication and project management",
                        "Scalable solutions built for growth",
                        "Rapid development and deployment cycles",
                        "Industry-leading security practices",
                        "24/7 monitoring and support",
                        "Cost-effective, value-driven solutions"
                      ].map((point, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <CheckCircle2 className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                          <p className="text-gray-300">{point}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-12 pt-12 border-t border-gray-700">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                        <Rocket className="w-6 h-6 text-slate-900" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Ready to get started?</p>
                        <p className="text-gray-400">We respond fast—usually within one business hour</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;